import { useState, useEffect, useRef } from "react";

interface CountUpProps {
  /** The final display value (can contain non-numeric chars like "0,1 mm") */
  value: string;
  /** Duration of the animation in ms */
  duration?: number;
  /** Delay before starting in ms */
  delay?: number;
  /** Additional className */
  className?: string;
}

/**
 * CountUp — Animated counter that counts up to a target value on scroll.
 * Extracts the numeric part, animates it, then displays the full formatted value.
 * Handles decimals, commas (Swedish format), and mixed text like "0,1 mm" or "<24h".
 */
export default function CountUp({
  value,
  duration = 1500,
  delay = 0,
  className = "",
}: CountUpProps) {
  const [displayValue, setDisplayValue] = useState(value);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || hasAnimated) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.unobserve(el);
          setTimeout(() => animate(), delay);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasAnimated, delay]);

  function animate() {
    // Extract numeric portion from value
    // Handle Swedish comma decimals like "0,1" and prefix chars like "<"
    const match = value.match(/([<>]?)(\d+(?:[,.]?\d+)?)/);
    if (!match) {
      // No numeric part found, just show the value
      setDisplayValue(value);
      setHasAnimated(true);
      return;
    }

    const prefix = match[1] || "";
    const numStr = match[2];
    const isDecimal = numStr.includes(",") || numStr.includes(".");
    const separator = numStr.includes(",") ? "," : ".";
    const numericValue = parseFloat(numStr.replace(",", "."));
    const decimalPlaces = isDecimal
      ? numStr.split(separator)[1]?.length || 0
      : 0;

    // Get the suffix (everything after the number)
    const numIndex = value.indexOf(numStr);
    const suffix = value.slice(numIndex + numStr.length);
    // Get any text before the prefix+number
    const beforePrefix = value.slice(0, numIndex - prefix.length);

    const startTime = performance.now();

    function step(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = numericValue * eased;

      let formatted: string;
      if (isDecimal) {
        formatted = current
          .toFixed(decimalPlaces)
          .replace(".", separator);
      } else {
        formatted = Math.round(current).toString();
      }

      setDisplayValue(`${beforePrefix}${prefix}${formatted}${suffix}`);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setDisplayValue(value);
        setHasAnimated(true);
      }
    }

    // Start with "0" version
    const zeroFormatted = isDecimal
      ? (0).toFixed(decimalPlaces).replace(".", separator)
      : "0";
    setDisplayValue(`${beforePrefix}${prefix}${zeroFormatted}${suffix}`);
    requestAnimationFrame(step);
  }

  return (
    <span ref={ref} className={className}>
      {displayValue}
    </span>
  );
}
